import { useEffect, useRef, useState } from "react";
import type { MapTestSession } from "../core/map-test";
import type { PickupZoneId } from "../core/pickup-map";

export function useMapTest(deviceId: string, settingsOpen: boolean, nowMs: number) {
	const [session, setSession] = useState<MapTestSession | null>(null);
	const [available, setAvailable] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [revision, setRevision] = useState(0);
	const generation = useRef(0);
	const writing = useRef(false);
	const activeSession = session && session.expiresAtMs > nowMs ? session : null;
	const active = !!activeSession?.joined;
	const enabled = settingsOpen || active;
	useEffect(() => {
		if (!enabled) return;
		let disposed = false;
		async function load() {
			if (writing.current) return;
			setLoading(true);
			const current = ++generation.current;
			try {
				const response = await fetch(`/api/map-test?deviceId=${encodeURIComponent(deviceId)}`);
				if (response.status === 403) {
					if (!disposed && current === generation.current) { setAvailable(false); setSession(null); }
					return;
				}
				if (!response.ok) throw new Error("Map test unavailable. Refresh to retry.");
				const body = await response.json() as { session: MapTestSession | null };
				if (!disposed && current === generation.current) { setSession(body.session); setAvailable(true); setError(null); }
			} catch {
				if (!disposed && current === generation.current) setError("Map test unavailable. Refresh to retry.");
			} finally {
				if (!disposed && current === generation.current) setLoading(false);
			}
		}
		void load();
		const visible = () => { if (document.visibilityState === "visible") void load(); };
		const timer = window.setInterval(visible, 15_000);
		document.addEventListener("visibilitychange", visible);
		return () => { disposed = true; ++generation.current; window.clearInterval(timer); document.removeEventListener("visibilitychange", visible); };
	}, [deviceId, enabled, revision]);
	async function act(action: "start" | "join" | "end" | "spot", zoneId?: PickupZoneId | null) {
		if (writing.current) return;
		writing.current = true;
		setSaving(true);
		setLoading(false);
		const current = ++generation.current;
		try {
			const response = await fetch("/api/map-test", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId, action, sessionId: activeSession?.id, zoneId, expectedAtMs: activeSession?.map?.spotChangedAtMs ?? null }) });
			if (!response.ok && response.status !== 409) throw new Error("Map test not updated. Refresh and try again.");
			const body = await response.json() as { session: MapTestSession | null; error: string | null };
			if (current === generation.current) { setSession(body.session); setError(body.error); }
		} catch (cause) {
			if (current === generation.current) setError(cause instanceof Error ? cause.message : "Map test not updated.");
		} finally { writing.current = false; setSaving(false); }
	}
	return { session: activeSession, active: active && !error, available, error, loading, saving, act,
		map: { data: error ? null : activeSession?.map ?? null, error, loading, saving, setSpot: (zoneId: PickupZoneId | null) => act("spot", zoneId), refresh: () => setRevision(value => value + 1) } };
}